import { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  message,
  Typography,
  Steps,
  Descriptions,
  Result,
  Select,
  Spin,
} from 'antd';
import useGetCards from '../hooks/cards/useGetCards';
import CardsList from '../components/cards/CardsList';
import { getCardIcon } from '@/utils/get-card-icon-util';
import CardsPendingRegistrationTable from '@/components/cards/CardsPendingRegistrationTable';
import CardsPhysicalCardsTable from '@/components/cards/CardsPhysicalCardsTable';
import CardsVirtualCardsTable from '@/components/cards/CardsVirtualCardsTable';
import CardsPendingFundingTable from '@/components/cards/CardsPendingFundingTable';
import DispatchTable from '@/components/cards/DispatchTable';
import DispatchedCardsTable from '@/components/cards/DispatchedCardsTable';
import useGetProductOrders from '@/hooks/cards/useGetProductOrders';
import { stats } from '@/utils/get-overview-stats-util';
import { usePickupLocations } from '@/hooks/cards/usePickupLocations';
import { TGetTransactionResponse } from '@/types/transactions/transaction-types';

const { Title } = Typography;

// Tabs to show on Cards view (Kadavra listing only; registration/dispatch tabs kept in code for future use)
const VISIBLE_CARD_TAB_KEYS = ['overview', 'physical', 'virtual'];

const Cards = () => {
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [registerModalVisible, setRegisterModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [cardType, setCardType] = useState('');
  const [modalStep, setModalStep] = useState(1); // 1 = card details, 2 = dispatch
  const [filter, setFilter] = useState({
    status: 'issued',
    type: '',
  });

  // Always call all hooks, but use enabled to control fetching
  const overviewQuery = useGetCards(
    { status: filter.status || 'issued', include_batch_cards: true },
    { enabled: activeTab === 'overview' }
  );
  const physicalCardsQuery = useGetCards(
    { card_type: 'physical', status: 'issued', include_batch_cards: true },
    { enabled: activeTab === 'physical' }
  );
  const virtualCardsQuery = useGetCards(
    { card_type: 'virtual', status: 'issued', include_batch_cards: true },
    { enabled: activeTab === 'virtual' }
  );
  // Always call useGetTransactions, fallback to always fetching if it doesn't support enabled
  const pendingFundingQuery = useGetCards(
    { status: 'pending-funding', card_type: 'physical', include_batch_cards: true, sort_by: 'delivery-date' },
    { enabled: activeTab === 'pending-funding' }
  );
  // Pending Dispatch
  const pendingDispatchSingleQuery = useGetCards(
    { status: 'pending-collection', card_type: 'physical', sort_by: 'delivery-date' },
    { enabled: activeTab === 'dispatch', staleTime: 2 * 60 * 1000 }
  );
  const pendingDispatchBatchQuery = useGetProductOrders(
    { status: 'pending-collection', sort_by: 'delivery-date' },
    { enabled: activeTab === 'dispatch', staleTime: 2 * 60 * 1000 }
  );
  // Dispatched Cards
  const dispatchedSingleQuery = useGetCards(
    { status: 'dispatched', card_type: 'physical', include_dispatch_log: true },
    { enabled: activeTab === 'dispatched', staleTime: 2 * 60 * 1000 }
  );
  const dispatchedBatchQuery = useGetProductOrders(
    { status: 'dispatched', include_dispatch_log: true },
    { enabled: activeTab === 'dispatched', staleTime: 2 * 60 * 1000 }
  );

  const handleApplyFilter = (status: string, type: string) => {
    // Apply filter - ensure Overview only shows issued cards by default
    setFilter({ 
      status: status || 'issued', 
      type 
    });
  };

  // Test card numbers for validation
  const TEST_CARDS = {
    VISA: ['4111111111111111', '4242424242424242'],
    MASTERCARD: ['5555555555554444', '2223000048400011'],
    VERVE: ['5061020000000000', '5061030000000000'],
  };

  // Card type detection with early detection
  const detectCardType = (number: string) => {
    const cleanNumber = number.replace(/\D/g, '');
    if (cleanNumber.length < 2) return '';

    const firstTwo = cleanNumber.substring(0, 2);
    const firstSix = cleanNumber.substring(0, 6);

    // Visa: Starts with 4
    if (cleanNumber[0] === '4') return 'VISA';

    // Mastercard: Starts with 51-55 or 2221-2720
    if (
      ['51', '52', '53', '54', '55'].includes(firstTwo) ||
      (firstSix >= '222100' && firstSix <= '272099')
    ) {
      return 'MASTERCARD';
    }

    // Verve: Common BIN ranges
    if (
      firstTwo === '50' || // Check early for potential Verve
      (firstSix >= '506099' && firstSix <= '506198') ||
      (firstSix >= '507865' && firstSix <= '507964') ||
      (firstSix >= '650002' && firstSix <= '650027')
    ) {
      return 'VERVE';
    }

    return '';
  };

  // Card number formatting and validation
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;

    // Remove all non-numeric characters
    const numericValue = value.replace(/\D/g, '');

    // Limit length to 19 digits
    if (numericValue.length <= 19) {
      // Format with spaces after every 4 digits
      const formattedValue = numericValue.replace(/(.{4})/g, '$1 ').trim();

      // Early card type detection
      const detectedType = detectCardType(numericValue);
      setCardType(detectedType);

      // Update form values with validation
      form.setFieldsValue({
        card_number: formattedValue,
      });

      // Validate length based on card type
      if (detectedType && numericValue.length >= 12) {
        const isValidLength =
          (detectedType === 'VISA' && [13, 16].includes(numericValue.length)) ||
          (detectedType === 'MASTERCARD' && numericValue.length === 16) ||
          (detectedType === 'VERVE' && [16, 19].includes(numericValue.length));

        if (!isValidLength) {
          form.setFields([
            {
              name: 'card_number',
              errors: ['Invalid card number length for ' + detectedType],
            },
          ]);
        }
      }
    }
  };

  // Prevent non-numeric input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      e.preventDefault();
    }
  };

  // PENDING REGISTRATIONS
  const handleRegisterCard = async (values: any, action = 'register') => {
    try {
      setLoading(true);
      const numericCardNumber = values.card_number.replace(/\D/g, '');

      // In production, this would call the API
      // await registerPhysicalCard({
      //   ...values,
      //   card_number: numericCardNumber,
      //   action, // 'register' or 'dispatch'
      // });

      message.success(`Card ${action}ed successfully`);
      // Refresh data
      setRegisterModalVisible(false);
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || `Failed to ${action} card`);
    } finally {
      setLoading(false);
    }
  };

  // Pickup locations logic for pending registration
  const { locations, loading: locationsLoading, error: locationsError } = usePickupLocations();
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // Set default location on load
  useEffect(() => {
    if (locations && locations.length > 0 && !selectedLocationId) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  const items = [
    {
      key: 'overview',
      label: 'Overview',
      children: (
        <>
          <Row gutter={[24, 24]}>
            {stats(overviewQuery.data || []).map((stat, index) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <Card bordered={false}>
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    valueStyle={{ color: stat.color }}
                    prefix={stat.icon}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          <CardsList
            cardData={overviewQuery.data || []}
            fetchNextPage={overviewQuery.fetchNextPage || (() => Promise.resolve({} as any))}
            hasNextPage={overviewQuery.hasNextPage || false}
            isFetchingNextPage={overviewQuery.isFetchingNextPage || false}
            handleApplyFilter={handleApplyFilter}
            isTableLoading={overviewQuery.isFetching || false}
            handleRefresh={() => {
              setFilter({ status: 'issued', type: '' });
            }}
            isRefreshLoadingButton={overviewQuery.isPending || false}
          />
        </>
      ),
    },
    {
      key: 'physical',
      label: 'Physical Cards',
      children: (
        <CardsPhysicalCardsTable
          cardData={physicalCardsQuery.data || []}
          isTableLoading={physicalCardsQuery.isFetching || false}
          fetchNextPage={physicalCardsQuery.fetchNextPage || (() => Promise.resolve({} as any))}
          hasNextPage={physicalCardsQuery.hasNextPage || false}
          isFetchingNextPage={physicalCardsQuery.isFetchingNextPage || false}
        />
      ),
    },
    {
      key: 'virtual',
      label: 'Virtual Cards',
      children: (
        <CardsVirtualCardsTable
          cardData={virtualCardsQuery.data || []}
          isTableLoading={virtualCardsQuery.isFetching || false}
          fetchNextPage={virtualCardsQuery.fetchNextPage || (() => Promise.resolve({} as any))}
          hasNextPage={virtualCardsQuery.hasNextPage || false}
          isFetchingNextPage={virtualCardsQuery.isFetchingNextPage || false}
        />
      ),
    },
    {
      key: 'pending-registration',
      label: 'Pending Registration',
      children: (
        <div>
          {locationsLoading ? (
            <Spin />
          ) : locationsError ? (
            <div style={{ color: 'red' }}>{locationsError}</div>
          ) : locations.length === 0 ? (
            <div>No pickup locations found.</div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <Select
                  value={selectedLocationId}
                  onChange={setSelectedLocationId}
                  style={{ minWidth: 250 }}
                  options={locations.map(loc => ({
                    value: loc.id,
                    label: `${loc.state} - ${loc.address_line1}${loc.city ? ', ' + loc.city : ''}`,
                  }))}
                />
              </div>
              <CardsPendingRegistrationTable dispatchAddressId={selectedLocationId} />
            </>
          )}
        </div>
      ),
    },
    // {
    //   key: 'pending-funding',
    //   label: 'Pending Funding',
    //   children: (
    //     <CardsPendingFundingTable
    //       cards={pendingFundingQuery.data || []}
    //       loading={pendingFundingQuery.isFetching || false}
    //       onSuccess={pendingFundingQuery.refetch}
    //     />
    //   ),
    // },
    {
      key: 'dispatch',
      label: 'Pending Dispatch',
      children: (
        <div>
          <Tabs
            defaultActiveKey="single"
            items={[
              {
                key: 'single',
                label: 'Single Cards',
                children: (
                  <DispatchTable
                    mode="single"
                    cardData={pendingDispatchSingleQuery.data || []}
                    fetchNextPage={pendingDispatchSingleQuery.fetchNextPage || (() => Promise.resolve({} as any))}
                    hasNextPage={pendingDispatchSingleQuery.hasNextPage || false}
                    isFetchingNextPage={pendingDispatchSingleQuery.isFetchingNextPage || false}
                    isTableLoading={pendingDispatchSingleQuery.isPending || false}
                  />
                ),
              },
              {
                key: 'batch',
                label: 'Batch Orders',
                children: (
                  <DispatchTable
                    mode="batch"
                    batchData={pendingDispatchBatchQuery.data || []}
                    fetchNextPageBatch={pendingDispatchBatchQuery.fetchNextPage || (() => Promise.resolve({} as any))}
                    hasNextPageBatch={pendingDispatchBatchQuery.hasNextPage || false}
                    isFetchingNextPageBatch={pendingDispatchBatchQuery.isFetchingNextPage || false}
                    isTableLoading={pendingDispatchBatchQuery.isFetching || false}
                    allCards={overviewQuery.data || []}
                  />
                ),
              },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'dispatched',
      label: 'Dispatched Cards',
      children: (
        <div>
          <Tabs
            defaultActiveKey="single"
            items={[
              {
                key: 'single',
                label: 'Single Cards',
                children: (
                  <DispatchedCardsTable
                    mode="single"
                    cardData={dispatchedSingleQuery.data || []}
                    fetchNextPage={dispatchedSingleQuery.fetchNextPage || (() => Promise.resolve({} as any))}
                    hasNextPage={dispatchedSingleQuery.hasNextPage || false}
                    isFetchingNextPage={dispatchedSingleQuery.isFetchingNextPage || false}
                    isTableLoading={dispatchedSingleQuery.isFetching || false}
                  />
                ),
              },
              {
                key: 'batch',
                label: 'Batch Orders',
                children: (
                  <DispatchedCardsTable
                    mode="batch"
                    batchData={dispatchedBatchQuery.data || []}
                    fetchNextPage={dispatchedBatchQuery.fetchNextPage || (() => Promise.resolve({} as any))}
                    hasNextPage={dispatchedBatchQuery.hasNextPage || false}
                    isFetchingNextPage={dispatchedBatchQuery.isFetchingNextPage || false}
                    isTableLoading={dispatchedBatchQuery.isFetching || false}
                  />
                ),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className='layout-content'>
      <Card bordered={false}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={items.filter((item) => VISIBLE_CARD_TAB_KEYS.includes(item.key))}
          size='large'
        />
      </Card>

      {/* NOTE: REVISIT THE BELOW ONCE THE PHYSICAL CARD IMPLEMENTATION IS READY ON FRONTOFFICE */}
      {/* CURRENTLY NOT USED FOR ANYTHING */}
      <Modal
        title={modalStep === 1 ? 'Register Physical Card' : 'Dispatch Card'}
        open={registerModalVisible}
        onCancel={() => {
          setRegisterModalVisible(false);
          form.resetFields();
          setCardType('');
          setModalStep(1);
        }}
        footer={[
          <Button
            key='cancel'
            onClick={() => {
              setRegisterModalVisible(false);
              form.resetFields();
              setCardType('');
              setModalStep(1);
            }}
          >
            Cancel
          </Button>,
          modalStep === 1 ? (
            <Button
              key='register'
              type='primary'
              onClick={async () => {
                try {
                  await handleRegisterCard(form.getFieldsValue(), 'register');
                  setModalStep(2);
                } catch (error) {
                  // Error is already handled in handleRegisterCard
                }
              }}
              loading={loading}
            >
              Register
            </Button>
          ) : (
            <Button
              key='dispatch'
              type='primary'
              onClick={() => {
                handleRegisterCard(form.getFieldsValue(), 'dispatch');
                setRegisterModalVisible(false);
                form.resetFields();
                setCardType('');
                setModalStep(1);
              }}
              loading={loading}
            >
              Dispatch
            </Button>
          ),
        ]}
      >
        <Form form={form} layout='vertical'>
          <div className='steps-indicator' style={{ marginBottom: '20px' }}>
            <Steps current={modalStep - 1} size='small'>
              <Steps.Step title='Register' />
              <Steps.Step title='Dispatch' />
            </Steps>
          </div>
          {modalStep === 1 && (
            <>
              <Form.Item
                name='card_number'
                label='Card Number'
                rules={[
                  { required: true, message: 'Please input the card number!' },
                  {
                    pattern: /^[0-9\s]{12,19}$/,
                    message: 'Card number must be between 12-19 digits!',
                  },
                  {
                    validator: async (_, value) => {
                      if (!value) return;
                      const numericValue = value.replace(/\D/g, '');
                      if (
                        numericValue.length < 12 ||
                        numericValue.length > 19
                      ) {
                        throw new Error(
                          'Card number must be between 12 and 19 digits'
                        );
                      }
                      const type = detectCardType(value);
                      if (!type) {
                        throw new Error('Invalid card number format');
                      }
                    },
                  },
                ]}
                help={
                  cardType ? `${cardType} card detected` : 'Enter card number'
                }
                validateStatus={cardType ? 'success' : ''}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      minWidth: '60px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s',
                    }}
                  >
                    {getCardIcon(cardType)}
                  </div>
                  <Input
                    placeholder='•••• •••• •••• ••••'
                    onChange={handleCardNumberChange}
                    onKeyPress={handleKeyPress}
                    maxLength={23} // 19 digits + 4 spaces
                    style={{
                      flex: 1,
                      fontSize: '16px',
                      letterSpacing: '1px',
                      fontFamily: 'monospace',
                    }}
                  />
                </div>
              </Form.Item>

              {cardType && (
                <div style={{ marginTop: '-12px', marginBottom: '16px' }}>
                  <Tag
                    color={
                      cardType === 'VISA'
                        ? '#1A1F71'
                        : cardType === 'MASTERCARD'
                        ? '#EB001B'
                        : '#2D9D3A'
                    }
                    style={{
                      fontSize: '0.9em',
                      padding: '2px 8px',
                      borderRadius: '4px',
                    }}
                  >
                    {cardType}
                  </Tag>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <Typography.Text type='secondary'>
                  Test Card Numbers:
                </Typography.Text>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {Object.entries(TEST_CARDS).map(([type, numbers]) => (
                    <li key={type}>
                      <Typography.Text copyable={{ text: numbers[0] }}>
                        {type}: {numbers[0]}
                      </Typography.Text>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}

          {modalStep === 2 && (
            <Result
              status='success'
              title='Card Registered Successfully!'
              subTitle='You can now dispatch this card to the customer'
              extra={
                <div
                  style={{
                    padding: '20px',
                    background: '#f9f9f9',
                    borderRadius: '8px',
                    marginBottom: '20px',
                  }}
                >
                  <Typography.Title level={5}>Card Details:</Typography.Title>
                  <Descriptions column={1}>
                    <Descriptions.Item label='Card Number'>
                      •••• {form.getFieldValue('card_number').slice(-4)}
                    </Descriptions.Item>
                    <Descriptions.Item label='Card Type'>
                      {cardType}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              }
            />
          )}

          {selectedTransaction && (
            <div
              style={{
                marginBottom: 24,
                background: '#f9f9f9',
                padding: '12px',
                borderRadius: '4px',
              }}
            >
              <Title level={5}>Transaction Details</Title>
              <p>
                <strong>Reference:</strong> {selectedTransaction.reference}
              </p>
              <p>
                <strong>Customer:</strong> {selectedTransaction.user_name}
              </p>
              <p>
                <strong>Amount:</strong>{' '}
                {selectedTransaction.dest_currency_code}{' '}
                {selectedTransaction.dest_amount.toLocaleString()}
              </p>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default Cards;
